/**
 * bugRef — deterministic, user-quotable reference id for a bug-report bundle.
 *
 * FR-FE-BUG-REPORT-UNIQUE-REF-SHOWN-TO-USER-2026-05-24
 *
 * Shape: `BUG-YYYY-MM-DD-XXXXX`
 *   - `YYYY-MM-DD` from the report `timestamp` (UTC date — same wall-clock
 *     anchor used everywhere else in the bundle, so support can grep logs
 *     by the date in the ref without timezone math).
 *   - `XXXXX` is a 5-char base32 suffix derived from a SHA-256 of
 *     `userId|timestamp|nonce`. SHA-256 because it's available in every
 *     browser (`crypto.subtle`) and in Node (jsdom test env) without a
 *     dependency; the 5-char window of 32^5 = 33.5M outcomes is large
 *     enough that within a single calendar day a single user is not going
 *     to collide on retry, while still being short enough to read aloud
 *     over Telegram / a phone call.
 *
 * WHY base32 (Crockford alphabet, no I/L/O/U): the ref is dictated over
 * voice — `0` vs `O` and `1` vs `I/L` are the two confusables that ruin a
 * support handoff. Crockford drops both pairs plus U (to avoid accidental
 * profanity). All-uppercase keeps it scannable.
 *
 * Server contract (read by the on-duty agent processing
 * scout_activity_log `bug_report_submitted` rows): the ref is the
 * authoritative external id of the bug manifest row. The agent landing
 * the row uses it as `id` in workbench `entities_create` so the user's
 * quoted ref resolves to a manifest entity 1:1.
 */

import type { ReportKind } from './debugReportCapture.types'

// Crockford base32, minus I, L, O, U.
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/**
 * Ref prefix per report kind. The digest mechanics are identical across
 * kinds — only the human-readable prefix differs so support / the triage
 * agent can tell a bug report (`BUG-`) from a feature request (`FR-`) at a
 * glance. The server-side intake keys on the FULL ref (`payload.bugRef`),
 * so adding a kind-aware prefix is back-compatible.
 */
const REF_PREFIX: Record<ReportKind, string> = { bug: 'BUG', feature: 'FR' }

/** UTC date portion of an ISO timestamp (`2026-05-24T18:00:00Z` → `2026-05-24`). */
function utcDateOf(iso: string): string {
  // Defensive: if a caller hands us a non-ISO string we still want a date
  // segment we can read, not a crash. Fall back to today's UTC date.
  const parsed = new Date(iso)
  const safe = Number.isFinite(parsed.getTime()) ? parsed : new Date()
  return safe.toISOString().slice(0, 10)
}

/**
 * Encode the first N bytes of a buffer as Crockford base32 (5 chars per
 * 5 bytes; we only need 5 chars so we read 4 bytes = 32 bits, which yields
 * ~6.4 base32 chars — we take the first 5).
 */
function encodeCrockford5(bytes: Uint8Array): string {
  let bits = 0
  let value = 0
  let out = ''
  for (let i = 0; i < bytes.length && out.length < 5; i++) {
    value = (value << 8) | bytes[i]
    bits += 8
    while (bits >= 5 && out.length < 5) {
      bits -= 5
      out += CROCKFORD[(value >>> bits) & 0x1f]
    }
  }
  // Edge case: if we somehow received fewer than 4 bytes (shouldn't happen
  // with SHA-256), pad with '0' so the ref shape is always 5 chars.
  while (out.length < 5) out += '0'
  return out
}

/**
 * Browser `crypto.subtle.digest` returns an ArrayBuffer. jsdom's stub
 * lacks subtle in some test setups, so we fall back to a tiny deterministic
 * non-cryptographic hash (FNV-1a 32-bit) — still distributes evenly over
 * the 5-char window for the volumes a single user produces.
 *
 * The fallback is NOT a security feature; the ref isn't a secret. We only
 * need stable, collision-resistant-enough quoting for support.
 */
async function digestBytes(input: string): Promise<Uint8Array> {
  const subtle = (globalThis.crypto as Crypto | undefined)?.subtle
  if (subtle && typeof subtle.digest === 'function') {
    const enc = new TextEncoder()
    const buf = await subtle.digest('SHA-256', enc.encode(input))
    return new Uint8Array(buf)
  }
  // FNV-1a 32-bit — packed into a 4-byte big-endian buffer.
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0
  }
  const out = new Uint8Array(4)
  out[0] = (h >>> 24) & 0xff
  out[1] = (h >>> 16) & 0xff
  out[2] = (h >>> 8) & 0xff
  out[3] = h & 0xff
  return out
}

export interface BugRefInputs {
  /** User id; null/undefined fall back to `'anon'` so the ref is still stable. */
  userId: string | null | undefined
  /** ISO timestamp of the report. Pinned by the caller so the ref is reproducible from the bundle. */
  timestamp: string
  /** Per-submit nonce; pass a fresh value (e.g. `crypto.randomUUID()`) per attempt. */
  nonce: string
  /** Report kind — selects the ref prefix. Defaults to `'bug'` (historical behaviour). */
  kind?: ReportKind
}

/**
 * Build a user-quotable reference. Async because the underlying digest
 * is async in the browser; callers `await` once at submit time.
 *
 * The `kind` only changes the prefix (`BUG-` vs `FR-`); the date + digest
 * suffix are computed identically, so the suffix uniqueness guarantees are
 * unchanged. Defaults to `'bug'` so every existing caller is untouched.
 */
export async function buildBugRef(input: BugRefInputs): Promise<string> {
  const date = utcDateOf(input.timestamp)
  const material = `${input.userId ?? 'anon'}|${input.timestamp}|${input.nonce}`
  const bytes = await digestBytes(material)
  const prefix = REF_PREFIX[input.kind ?? 'bug']
  return `${prefix}-${date}-${encodeCrockford5(bytes)}`
}

/**
 * Generate a per-submit nonce. Prefers `crypto.randomUUID`, falls back to
 * a `Math.random` mix when running under a stripped-down test runtime.
 */
export function newBugRefNonce(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  // Fallback: 16 random bytes hex-encoded. Not cryptographic; see digestBytes note.
  let s = ''
  for (let i = 0; i < 16; i++) s += Math.floor(Math.random() * 256).toString(16).padStart(2, '0')
  return s
}

/**
 * Shape validator — useful for tests and for the server-side intake.
 *
 * Crockford alphabet excludes I, L, O, U; we express the class as
 * `0-9 A-H J K M N P-T V-Z` to keep `L` and the other bans out (a
 * naïve `[0-9A-HJ-NP-TV-Z]` would still match `L` because `J-N`
 * spans it).
 */
export const BUG_REF_PATTERN = /^BUG-\d{4}-\d{2}-\d{2}-[0-9A-HJKMNP-TV-Z]{5}$/

/** Feature-request ref shape (`FR-YYYY-MM-DD-XXXXX`). */
export const FEATURE_REF_PATTERN = /^FR-\d{4}-\d{2}-\d{2}-[0-9A-HJKMNP-TV-Z]{5}$/

/** Any report ref (bug OR feature) — used by the server-side intake. */
export const REPORT_REF_PATTERN = /^(?:BUG|FR)-\d{4}-\d{2}-\d{2}-[0-9A-HJKMNP-TV-Z]{5}$/

/** True for a well-formed report ref of EITHER kind. */
export function isBugRef(value: unknown): value is string {
  return typeof value === 'string' && REPORT_REF_PATTERN.test(value)
}
